// The cap→slots calculator, extracted so there is exactly ONE definition of the earn split
// (LB-132, 31 Aug).
//
// WHY IT LIVES IN ITS OWN FILE. spendingDefaults.js imports utils.js, so utils.js cannot
// import spendingDefaults.js back — and seeding lives in utils.js. Without this module the
// only options were to duplicate the split maths in the seeder or to move seeding; a second
// copy of EARN_SPLIT arithmetic is exactly the thing the guardrails scope warns about, since
// the two would drift and nobody would notice until a household was priced wrong.
//
// Both sides now import from here: suggestedValues() (what the wizard and review page
// OFFER) and seedStandardSet() (what a new child actually GETS). Before this, those two
// answers were different — the split was only ever a suggestion, and seeding ignored it.
import { penceToTokens, spendingCapPence } from "./utils.js";
import { EARN_SPLIT, MIN_REWARD_TOKENS } from "./constants.js";

export const clampMin = (n) => Math.max(MIN_REWARD_TOKENS, Math.round(n));

export function capTokensFor(child, rate) {
  const pence = spendingCapPence(child);
  return pence === null ? null : penceToTokens(pence, rate);
}

// Divides a weekly cap into the four earning slots.
//
// `payouts` is how many times a week each subject can pay out, and dividing by it is the
// step that is easy to forget: a subject set to 3 payouts a week at 15 tokens each is 45
// tokens of cap, not 15. The slot targets are WEEKLY; the per-payout rewards are not.
//
// The task slot is the REMAINDER rather than its own percentage, so the four always sum to
// exactly the cap however the first three round.
export function splitForCap(capTokens, payouts) {
  if (capTokens === null || capTokens === undefined) return null;
  const p = { english: Math.max(1, payouts?.english || 1), maths: Math.max(1, payouts?.maths || 1) };
  // A cap this small cannot carry four meaningful slots — every one would round to 1 or 0.
  // Return the floor set and say so, rather than emitting nonsense a parent has to undo.
  if (capTokens < MIN_REWARD_TOKENS * 5) {
    return { capTooSmall: true, capTokens, englishWeekly: MIN_REWARD_TOKENS, mathsWeekly: MIN_REWARD_TOKENS, englishPerPayout: MIN_REWARD_TOKENS, mathsPerPayout: MIN_REWARD_TOKENS, habit: MIN_REWARD_TOKENS, taskSlot: MIN_REWARD_TOKENS, payouts: p };
  }
  const englishWeekly = Math.round(EARN_SPLIT.english * capTokens);
  const mathsWeekly = Math.round(EARN_SPLIT.maths * capTokens);
  const habit = clampMin(EARN_SPLIT.habit * capTokens);
  const taskSlot = Math.max(MIN_REWARD_TOKENS, capTokens - englishWeekly - mathsWeekly - habit);
  return {
    capTooSmall: false,
    capTokens,
    englishWeekly,
    mathsWeekly,
    englishPerPayout: clampMin(englishWeekly / p.english),
    mathsPerPayout: clampMin(mathsWeekly / p.maths),
    habit,
    taskSlot,
    payouts: p,
  };
}

// Distributes a task slot across a set of weighted tasks.
//
// The LAST task takes the remainder rather than its own weighted share, for the same reason
// the task slot itself is a remainder: rounding four weights independently leaves the set a
// token or two off the slot, and "a busy week comes to 49 of your 50" is a worse thing to
// show a parent than a task priced one token differently from its weight.
//
// The MIN_REWARD_TOKENS floor can still push a very small cap over its slot. That is the
// right way round — a task worth less than the floor is not worth doing — and the health
// line will show it rather than the seeder hiding it.
export function distributeTaskSlot(taskSlot, weights) {
  if (!weights || !weights.length) return [];
  const out = weights.slice(0, -1).map((w) => clampMin(taskSlot * w));
  const used = out.reduce((a, b) => a + b, 0);
  out.push(clampMin(taskSlot - used));
  return out;
}
